import React, {useState} from 'react';
import {Stack} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
  IconName,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {CONTENT_STUDIO_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import {Folder} from '@/api/fetchers/content-studio/folder';
import DropdownButton from '@/components/dropdown-button';
import useFoldersQuery from '@/hooks/content-studio/use-folders-query';
import useNavigation from '@/hooks/use-navigation';
import {contentStudioActions} from '@/stores/contentStudioStore';
import FolderCard from '../folder-card';

enum SortId {
  Newest = 'newest',
  Oldest = 'oldest',
  AtoZ = 'a-z',
  ZtoA = 'z-a',
}

const sortIcons: Record<SortId, IconName> = {
  [SortId.Newest]: 'Features-Duration',
  [SortId.Oldest]: 'Features-Duration',
  [SortId.AtoZ]: 'Navigation-Sort-Descending',
  [SortId.ZtoA]: 'Navigation-Sort-Ascending',
};

const sortMethods: Record<SortId, (a: Folder, b: Folder) => number> = {
  [SortId.Newest]: (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  [SortId.Oldest]: (a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  [SortId.AtoZ]: (a, b) => a.name.localeCompare(b.name),
  [SortId.ZtoA]: (a, b) => b.name.localeCompare(a.name),
};

type SortOption = {
  id: SortId;
  label: string;
};

const sortOptions: SortOption[] = [
  {
    id: SortId.Newest,
    label: translate('Newest'),
  },
  {
    id: SortId.Oldest,
    label: translate('Oldest'),
  },
  {
    id: SortId.AtoZ,
    label: translate('Alphabetically (A-Z)'),
  },
  {
    id: SortId.ZtoA,
    label: translate('Alphabetically (Z-A)'),
  },
];

function Projects(): JSX.Element {
  const {navigate} = useNavigation();
  const [sortOption, setSortOption] = useState<SortOption>(sortOptions[0]);

  const {data: folders, isLoading, error} = useFoldersQuery();
  const {uiInteraction} = useUiInteractionEvent();

  if (isLoading) return <div>Loading projects...</div>;
  if (error)
    return <div>Error loading projects: {(error as Error).message}</div>;

  return (
    <Stack
      direction="column"
      gap="16px"
      width="100%"
      maxWidth="1088px"
      marginTop="48px"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Headline size="lg" color={designSystemToken('semantic.fg.primary')}>
          {translate('Projects')}
        </Headline>
        <DropdownButton
          label={translate('Sort by')}
          size="medium"
          options={sortOptions}
          renderMenuItem={(option) => {
            const icon = sortIcons[option.id];
            return (
              <Stack direction="row" gap="10px">
                <Icon name={icon} size="medium" appearance="neutral" />
                {option.label}
              </Stack>
            );
          }}
          onChange={(option) => {
            setSortOption(option);
            uiInteraction({
              item_name: 'projects_sort_by',
              interaction_location: CONTENT_STUDIO_VIEW_NAMES.HOME,
              interaction_details: option.label,
            });
          }}
        />
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap="16px">
        {folders
          .filter((folder) => folder.filesCount > 0)
          .sort(sortMethods[sortOption.id])
          .map((folder: Folder) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              onClick={() => {
                uiInteraction({
                  item_name: 'project_clicked',
                  interaction_location: CONTENT_STUDIO_VIEW_NAMES.HOME,
                  asset_id: folder.id,
                });
                contentStudioActions.setProjectFilters(folder.name);
                navigate(`${ROUTES.CONTENT_LAB_ASSETS}?contentType=Results`);
              }}
            />
          ))}
      </Stack>
    </Stack>
  );
}

export default Projects;
